import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../common/hooks/hooks";
import { useUpdatePaymentMethodMutation } from "../api/checkoutApi";
import { useMutationWithFeedback } from "../../../../common/hooks/useMutationWithFeedback";
import { selectCustomerNote } from "../checkoutSelectors";

/**
 * Hook to handle updating the payment method on the server.
 * It automatically preserves the existing order note (from Redux) to prevent it from being
 * wiped during the server-side total recalculation.
 */
export const useUpdatePaymentMethod = () => {
  const { t } = useTranslation();

  // 1. Get the current note from Redux
  const currentNote = useAppSelector(selectCustomerNote);

  const [updatePaymentMethod, mutationResult] =
    useUpdatePaymentMethodMutation();

  const { trigger, isLoading } = useMutationWithFeedback({
    mutation: [updatePaymentMethod, mutationResult],
    // No loading toast here to keep radio switching feeling snappy,
    // but we map errors if the server rejects the method.
    errorMessages: {
      default: t(
        "feedback.checkout.paymentMethodUpdateError",
        "Could not update payment method. Please try again."
      ),
    },
  });

  const handleUpdatePaymentMethod = async (methodId: string) => {
    // 2. Send both the new method and the existing note to the API
    return await trigger({
      paymentMethod: methodId,
      customerNote: currentNote,
    });
  };

  return {
    handleUpdatePaymentMethod,
    isUpdating: isLoading,
  };
};
